import { useSnackbar, Typography, TextField } from '@fdha/web-ui-library';
import { Box, Button, useTheme } from '@mui/material';
import React, { useState } from 'react';
import {
  DeliveryV2,
  useCancelDeliveryBulkV2Mutation,
  useReactivateDeliveryBulkMutation,
} from '@fdha/graphql-api-admin';
import { Icon } from '@fdha/web-ui-library';
import { useApolloClient } from '@apollo/client';
import { grey } from '@mui/material/colors';

import DeliveryTable from './DeliveryTable';

export type DeliveryV2Admin = Omit<
  DeliveryV2,
  'dietPlanDeliveryMenu' | 'foodProgramUser'
> & {
  foodProgramUser: {
    patient: {
      subject_id?: string | null | undefined;
    };
  };
};

interface AffectedDeliveriesProps {
  uncoveredDeliveries: DeliveryV2Admin[];
  withinDeliveries: DeliveryV2Admin[];
  partiallyWithinDeliveries: DeliveryV2Admin[];
  plannedPauseRange: string[];
  closeDialog?: () => void;
}

interface TableHelperComponentProps {
  deliveries: DeliveryV2Admin[];
  title: string;
  description: string;
  getWeekOfCycle: (delivery: DeliveryV2Admin) => string;
  selectedIds: string[];
  onToggleSelection: (deliveryId: string) => void;
  onSelectAll: (checked: boolean) => void;
  isCancelation: boolean;
}

interface PlannedPauseRangeGroupProps {
  plannedPauseRange: string[];
}

interface PlannedPauseRangeProps {
  range: string;
  index: number;
}

const TableHelperComponent: React.FC<TableHelperComponentProps> = ({
  deliveries,
  title,
  description,
  getWeekOfCycle,
  selectedIds,
  onToggleSelection,
  onSelectAll,
  isCancelation,
}) => {
  const theme = useTheme();
  const bgcolor = isCancelation
    ? theme.palette.error.lightBg
    : theme.palette.success.lightBg;
  const iconName = isCancelation ? 'slash-outline' : 'car-outline';
  const iconFill = isCancelation
    ? theme.palette.error.main
    : theme.palette.success.main;

  return (
    <>
      {deliveries.length > 0 && (
        <Box
          mb={4}
          paddingTop={2}
          bgcolor={bgcolor}
          border={`2px solid ${grey[300]}`}
          borderRadius={1}
          overflow="hidden"
        >
          <Box display="flex" alignItems="center" mb={1} px={3} py={2}>
            <Box display="flex" alignItems="center" sx={{ width: 32, mr: 1 }}>
              <Icon name={iconName} fill={iconFill} size="large" />
            </Box>
            <Box>
              <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                {title}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {description}
              </Typography>
            </Box>
          </Box>

          <DeliveryTable
            deliveries={deliveries}
            selectedIds={selectedIds}
            onToggleSelection={onToggleSelection}
            onSelectAll={onSelectAll}
            getWeekOfCycle={getWeekOfCycle}
            isCancelation={isCancelation}
          />
        </Box>
      )}
    </>
  );
};

const PlannedPauseRange: React.FC<PlannedPauseRangeProps> = ({
  range,
  index,
}) => {
  const theme = useTheme();

  return (
    <Box key={index} mr={1} mb={1} display="flex">
      <Icon
        name="calendar-outline"
        fill={theme.palette.text.secondary}
        size="large"
      />
      <Box component="span" sx={{ ml: 0.5 }}>
        {range}
      </Box>
    </Box>
  );
};

const PlannedPauseRangeGroup: React.FC<PlannedPauseRangeGroupProps> = ({
  plannedPauseRange = [],
}) => {
  return (
    <>
      <Typography fontWeight="bold" mr={1}>
        Planned pause:
      </Typography>
      {plannedPauseRange.length > 0 ? (
        plannedPauseRange.map((range, index) => {
          return (
            <>
              <PlannedPauseRange key={index} range={range} index={index} />
              {index < plannedPauseRange.length - 1 && (
                <Typography component="span" sx={{ mr: 0.5 }}>
                  ,
                </Typography>
              )}
            </>
          );
        })
      ) : (
        <PlannedPauseRange range="None" index={0} />
      )}
    </>
  );
};

const AffectedDeliveriesModal: React.FC<AffectedDeliveriesProps> = ({
  uncoveredDeliveries,
  withinDeliveries,
  partiallyWithinDeliveries,
  plannedPauseRange,
  closeDialog,
}) => {
  const [disabledConfirmButton, setDisabledConfirmButton] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [comment, setComment] = useState('');
  const [selectedToReactivate, setSelectedToReactivate] = useState<string[]>(
    uncoveredDeliveries?.map((delivery) => delivery.id) || []
  );
  const [selectedToCancel, setSelectedToCancel] = useState<string[]>(
    withinDeliveries?.map((delivery) => delivery.id) || []
  );
  const apolloClient = useApolloClient();
  const theme = useTheme();
  const [cancelDeliveryBulk] = useCancelDeliveryBulkV2Mutation();
  const [reactivateDeliveryBulk] = useReactivateDeliveryBulkMutation();

  const allCancelableDeliveries = [
    ...withinDeliveries,
    ...partiallyWithinDeliveries.map((delivery) => ({
      ...delivery,
      isPartiallyWithin: true,
    })),
  ];

  const handleToggleReactivate = (deliveryId: string) => {
    setSelectedToReactivate((prev) =>
      prev.includes(deliveryId)
        ? prev.filter((id) => id !== deliveryId)
        : [...prev, deliveryId]
    );
  };

  const handleToggleCancel = (deliveryId: string) => {
    setSelectedToCancel((prev) =>
      prev.includes(deliveryId)
        ? prev.filter((id) => id !== deliveryId)
        : [...prev, deliveryId]
    );
  };

  const handleSelectAllReactivate = (checked: boolean) => {
    setSelectedToReactivate(
      checked ? uncoveredDeliveries.map((delivery) => delivery.id) : []
    );
  };

  const handleSelectAllCancel = (checked: boolean) => {
    if (!checked) {
      setSelectedToCancel([]);
      return;
    }

    const allIds = [
      ...withinDeliveries.map((delivery) => delivery.id),
      ...partiallyWithinDeliveries.map((delivery) => delivery.id),
    ];
    setSelectedToCancel(allIds);
  };

  const getWeekOfCycle = (delivery: DeliveryV2Admin) => {
    return delivery.weekOfCycle ? `Week ${delivery.weekOfCycle}` : 'N/A';
  };

  //! TODO: This is a temporary solution to get the subject id. We need to find a better way to do this. Possible solution:
  //! - Send the subject id in the top layer of the return object.
  const getSubjectId = (): string => {
    for (const delivery of [
      ...uncoveredDeliveries,
      ...withinDeliveries,
      ...partiallyWithinDeliveries,
    ]) {
      if (delivery.foodProgramUser?.patient?.subject_id) {
        return delivery.foodProgramUser?.patient?.subject_id;
      }
    }
    return '';
  };

  const handleSubmit = async () => {
    try {
      setDisabledConfirmButton(true);
      const subjectId = getSubjectId();

      const trimmedComment = comment.trim() || null;

      if (selectedToCancel.length > 0) {
        await cancelDeliveryBulk({
          variables: {
            deliveryIds: selectedToCancel,
            subjectId,
            isCanceledUpdateComment: trimmedComment,
          },
        });
      }

      if (selectedToReactivate.length > 0) {
        await reactivateDeliveryBulk({
          variables: {
            deliveryIds: selectedToReactivate,
            subjectId,
            isCanceledUpdateComment: trimmedComment,
          },
        });
      }

      apolloClient.cache.evict({ fieldName: 'deliveriesV2' });

      showSnackbar({
        message: 'Deliveries updated successfully',
        severity: 'success',
      });
      closeDialog?.();
    } catch (error: unknown) {
      showSnackbar({
        message:
          error instanceof Error ? error.message : 'Error updating deliveries',
        severity: 'error',
      });
    } finally {
      setDisabledConfirmButton(false);
    }
  };

  return (
    <>
      <Box mb={3}>
        <Typography variant="body1" color={theme.palette.text.disabled}>
          You've added, edited or removed a planned pause that affects existing
          deliveries. Please choose how you would like to manage them.
        </Typography>
      </Box>
      <Box mb={3} display="flex" flexWrap="wrap">
        <PlannedPauseRangeGroup plannedPauseRange={plannedPauseRange} />
      </Box>
      {!!uncoveredDeliveries.length && (
        <TableHelperComponent
          deliveries={uncoveredDeliveries}
          title="Reactivate deliveries"
          description="Choose the deliveries that you want to reactivate. This delivery will show as active to the subject."
          isCancelation={false}
          selectedIds={selectedToReactivate}
          onToggleSelection={handleToggleReactivate}
          onSelectAll={handleSelectAllReactivate}
          getWeekOfCycle={getWeekOfCycle}
        />
      )}
      {!!allCancelableDeliveries.length && (
        <TableHelperComponent
          deliveries={allCancelableDeliveries}
          title="Cancel deliveries"
          description="Choose the deliveries that you want to cancel. This delivery will show as cancelled to the subject."
          isCancelation={true}
          selectedIds={selectedToCancel}
          onToggleSelection={handleToggleCancel}
          onSelectAll={handleSelectAllCancel}
          getWeekOfCycle={getWeekOfCycle}
        />
      )}

      <Box mb={2}>
        <Typography variant="subtitle2" mb={1}>
          Comment (optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          placeholder="Enter any additional information to support Food Ops team"
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          inputProps={{ maxLength: 120 }}
          helperText={`${comment.length}/120 characters limit`}
          size="medium"
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" marginTop={5}>
        <Button color="primary" size="large" onClick={closeDialog}>
          CANCEL AND KEEP CURRENT STATE
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="large"
          type="submit"
          onClick={handleSubmit}
          disabled={disabledConfirmButton}
        >
          CONFIRM AND APPLY CHANGES
        </Button>
      </Box>
    </>
  );
};

export default AffectedDeliveriesModal;
